import { hexLen, hexToWorld, type Hex } from '../hex.ts';
import { idiv, imax, imin, isqrt } from '../math/fixed.ts';
import { deriveSeed, rngInt, rngRange, seedRng, type RngState } from '../math/rng.ts';
import type { Pt } from '../caza-types.ts';
import type { LagoonCross, LagoonTrack } from '../isla-types.ts';

/**
 * The Long Hunt's chapter 4 grounds from the seed (docs/sendas/barbaro.md, «La Isla Blanca»): the island's shore with
 * Sigrun's camp and three floes off it joined by necks of ice (act 1), the clear lagoon with the tracks frozen under it
 * (act 2), the island by night with her fire and the open cracks (act 3). The fight's builder
 * (campaign/sendas-caza.ts) and the renderer build them the same way. Integer maths only.
 */

export const FLOES_RADIUS = 11;
export const LAGOON_RADIUS = 12;
export const SEAL_RADIUS = 11;

const allCells = (R: number): Hex[] => {
  const out: Hex[] = [];
  for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (hexLen(q, r) <= R) out.push({ q, r });
  return out;
};

const key = (h: Hex): string => `${h.q},${h.r}`;
const len = (dx: number, dy: number): number => isqrt(dx * dx + dy * dy);

/** The board's rocks (the sea, open water): every cell of the map (its radius + 2) that is not floor. */
const rocksBut = (R: number, floor: Set<string>): Hex[] => allCells(R + 2).filter((h) => !floor.has(key(h)));

/** The squared distance from a point to a segment (integer). */
export const segDist2 = (px: number, py: number, a: Pt, b: Pt): number => {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const L2 = dx * dx + dy * dy;
  const tn = (px - a.x) * dx + (py - a.y) * dy;
  let cx = a.x;
  let cy = a.y;
  if (L2 > 0 && tn >= L2) {
    cx = b.x;
    cy = b.y;
  } else if (L2 > 0 && tn > 0) {
    cx = a.x + idiv(dx * tn, L2);
    cy = a.y + idiv(dy * tn, L2);
  }
  return (px - cx) * (px - cx) + (py - cy) * (py - cy);
};

/** Where two segments cross (strictly inside both), or null. */
const cross = (a1: Pt, a2: Pt, b1: Pt, b2: Pt): Pt | null => {
  const rx = a2.x - a1.x;
  const ry = a2.y - a1.y;
  const sx = b2.x - b1.x;
  const sy = b2.y - b1.y;
  let den = rx * sy - ry * sx;
  if (den === 0) return null;
  const qx = b1.x - a1.x;
  const qy = b1.y - a1.y;
  let tn = qx * sy - qy * sx;
  let un = qx * ry - qy * rx;
  if (den < 0) {
    den = -den;
    tn = -tn;
    un = -un;
  }
  if (tn <= 0 || tn >= den || un <= 0 || un >= den) return null;
  return { x: a1.x + idiv(rx * tn, den), y: a1.y + idiv(ry * tn, den) };
};

/* ---------- act 1: the floes ---------- */

export interface FloesLayout {
  rocks: Hex[];
  shore: Hex[];
  camp: Pt;
  campCells: Hex[];
  floes: Pt[];
  floeCells: Hex[][];
  spawns: Pt[];
  necks: Hex[][];
  neckAt: Pt[];
  landing: Pt[];
  offsets: number[];
  start: Pt;
  seats: Record<string, Pt>;
}

/** The floes' middles (west, north, east) and where each neck meets the shore. */
const FLOE_AT: Pt[] = [
  { x: -6000, y: -3400 },
  { x: 0, y: -5600 },
  { x: 6000, y: -3400 },
];
const NECK_TO: Pt[] = [
  { x: -3500, y: 1732 },
  { x: 0, y: 1732 },
  { x: 3500, y: 1732 },
];
const FLOE_R = 2150;
const SHORE_Y = 1700;

export const floesLayout = (seed: number, cycle: number): FloesLayout => {
  const rng = seedRng(deriveSeed(seed, 0x15a1));
  const R = FLOES_RADIUS;
  const shore: Hex[] = [];
  const floeCells: Hex[][] = FLOE_AT.map(() => []);
  const necks: Hex[][] = FLOE_AT.map(() => []);
  const camp = { x: 0, y: 6928 };
  const campCells: Hex[] = [];
  for (const h of allCells(R)) {
    const p = hexToWorld(h.q, h.r);
    // The shore: the island's edge, a little higher toward its middle.
    const edge = SHORE_Y + (Math.abs(p.x) > 7000 ? 900 : 0);
    if (p.y >= edge) {
      shore.push(h);
      if (len(p.x - camp.x, p.y - camp.y) <= 1150) campCells.push(h);
      continue;
    }
    const f = FLOE_AT.findIndex((c) => len(p.x - c.x, p.y - c.y) <= FLOE_R);
    if (f >= 0) {
      (floeCells[f] as Hex[]).push(h);
      continue;
    }
    const n = FLOE_AT.findIndex((c, k) => segDist2(p.x, p.y, c, NECK_TO[k] as Pt) <= 560 * 560);
    if (n >= 0) (necks[n] as Hex[]).push(h);
  }
  const floor = new Set([...shore, ...floeCells.flat(), ...necks.flat()].map(key));
  // Each neck its own place in the cycle: a third apart, a little off from the seed.
  const third = idiv(cycle, 3);
  const base = rngInt(rng, cycle);
  const order = [0, 1, 2];
  for (let i = 2; i > 0; i--) {
    const j = rngInt(rng, i + 1);
    const t = order[i] as number;
    order[i] = order[j] as number;
    order[j] = t;
  }
  const offsets = order.map((k) => (base + k * third + rngRange(rng, 0, idiv(third, 4))) % cycle);
  const neckAt = FLOE_AT.map((c, k) => {
    const t = NECK_TO[k] as Pt;
    return { x: c.x + idiv((t.x - c.x) * 55, 100), y: c.y + idiv((t.y - c.y) * 55, 100) };
  });
  const spawns = FLOE_AT.map((c) => {
    const d = imax(1, len(c.x, c.y));
    return { x: c.x + idiv(c.x * 1300, d), y: c.y + idiv(c.y * 1300, d) };
  });
  return {
    rocks: rocksBut(R, floor),
    shore,
    camp,
    campCells,
    floes: FLOE_AT.map((p) => ({ ...p })),
    floeCells,
    spawns,
    necks,
    neckAt,
    landing: NECK_TO.map((p) => ({ ...p })),
    offsets,
    start: { x: 0, y: 3900 },
    seats: { camp: { ...camp }, bera: { x: -1500, y: 7400 }, terron: { x: -2300, y: 7000 }, sten: { x: 1500, y: 7400 } },
  };
};

/* ---------- act 2: the lagoon ---------- */

export interface LagoonLayout {
  rocks: Hex[];
  /** The lagoon's ice (the rest of the floor is the island's snowy shore, to the south). */
  ice: Hex[];
  tracks: LagoonTrack[];
  crossings: LagoonCross[];
  ends: Pt[];
  last: number;
  shore: Pt[];
  start: Pt;
}

/** How long a track is (its segments). */
const trackLen = (pts: Pt[]): number => {
  let n = 0;
  for (let i = 1; i < pts.length; i++) n += len((pts[i] as Pt).x - (pts[i - 1] as Pt).x, (pts[i] as Pt).y - (pts[i - 1] as Pt).y);
  return n;
};

/** A point within `r` of the middle (a square cut to the circle). */
const inside = (rng: RngState, r: number, ylo = -r, yhi = r): Pt => {
  for (let k = 0; k < 20; k++) {
    const p = { x: rngRange(rng, -r, r), y: rngRange(rng, ylo, yhi) };
    if (len(p.x, p.y) <= r) return p;
  }
  return { x: 0, y: idiv(ylo + yhi, 2) };
};

const LAGOON_ICE = 8400;
const SHORE_LAGOON = 7600;

/**
 * One draw of the lagoon's tracks: Sigrun's six from the shore into the lagoon (long the first days, when she still
 * walked far; short the last), the Wolf's two across it end to end (he came after: over all of hers), the pack's two.
 */
/** Whether a track keeps `near` away from every end but its own (`own`, or −1). */
const clearOf = (pts: Pt[], ends: Pt[], own: number, near: number): boolean => {
  for (let k = 0; k < ends.length; k++) {
    if (k === own) continue;
    const e = ends[k] as Pt;
    for (let p = 1; p < pts.length; p++) if (segDist2(e.x, e.y, pts[p - 1] as Pt, pts[p] as Pt) < near * near) return false;
  }
  return true;
};

const drawTracks = (rng: RngState): LagoonTrack[] => {
  const out: LagoonTrack[] = [];
  // Her six ends first, spread over the lagoon; then each walked from the south shore to its end, from the other side
  // (so they cross in the middle), keeping clear of the other ends. The days go by length: the longest walked first;
  // the two longest bend on the way.
  const ends: Pt[] = [];
  for (let t = 0; t < 300 && ends.length < 6; t++) {
    const p = inside(rng, LAGOON_ICE - 900, -LAGOON_ICE + 600, SHORE_LAGOON - 2600);
    if (ends.every((e) => len(e.x - p.x, e.y - p.y) >= 2900)) ends.push(p);
  }
  ends.sort((a, b) => a.y - b.y || a.x - b.x);
  ends.forEach((e, d) => {
    let pts: Pt[] = [];
    for (let t = 0; t < 40; t++) {
      const a = { x: rngRange(rng, -6500, 6500), y: rngRange(rng, 6100, 7000) };
      const c: Pt[] = [a];
      if (d < 2) {
        const mx = idiv(a.x + e.x, 2) + rngRange(rng, -3500, 3500);
        const my = idiv(a.y + e.y, 2) + rngRange(rng, -1200, 1200);
        if (len(mx, my) < LAGOON_ICE - 800) c.push({ x: mx, y: my });
      }
      c.push(e);
      pts = c;
      if (clearOf(c, ends, d, 650)) break;
    }
    out.push({ pts, who: 0, rank: 0, letter: -1, day: 0 });
  });
  out.sort((x, y) => trackLen(y.pts) - trackLen(x.pts));
  out.forEach((t, d) => {
    t.rank = d * 2;
    t.day = d;
  });
  // The Wolf's: across, edge to edge (he walked the lagoon after her: over all of hers); the pack's, of any day.
  for (let k = 0; k < 2; k++) {
    const wolf = k === 0;
    let pts: Pt[] = [];
    for (let t = 0; t < 60; t++) {
      const s0 = { x: rngRange(rng, -LAGOON_ICE + 900, LAGOON_ICE - 900), y: -LAGOON_ICE + 1400 };
      const s1 = { x: rngRange(rng, -LAGOON_ICE + 900, LAGOON_ICE - 900), y: SHORE_LAGOON - 900 };
      pts = wolf ? [s0, inside(rng, 4000), s1] : [s1, s0];
      if (clearOf(pts, ends, -1, 500)) break;
    }
    out.push({ pts, who: wolf ? 1 : 2, rank: wolf ? 100 : rngRange(rng, 0, 5) * 2 + 1, letter: -1, day: -1 });
  }
  return out;
};

/** Every crossing of two tracks of which one at least is hers (the Wolf's and the pack's among themselves aren't read). */
export const crossingsOf = (tracks: LagoonTrack[]): LagoonCross[] => {
  const out: LagoonCross[] = [];
  for (let i = 0; i < tracks.length; i++)
    for (let j = i + 1; j < tracks.length; j++) {
      const A = tracks[i] as LagoonTrack;
      const B = tracks[j] as LagoonTrack;
      if (A.who !== 0 && B.who !== 0) continue;
      for (let p = 1; p < A.pts.length; p++)
        for (let q = 1; q < B.pts.length; q++) {
          const c = cross(A.pts[p - 1] as Pt, A.pts[p] as Pt, B.pts[q - 1] as Pt, B.pts[q] as Pt);
          // (The same two crossing again by a bend read as one crossing.)
          if (c && !out.some((o) => o.a === i && o.b === j && len(o.at.x - c.x, o.at.y - c.y) < 1600)) out.push({ at: c, a: i, b: j, top: A.rank > B.rank ? i : j });
        }
    }
  return out;
};

/**
 * Whether a draw reads one way only (§4, «La semilla»): each of her tracks but the last walked over by a later one of
 * hers, at a crossing that reads (crossings apart from each other and from the ends, the ends apart); the last the
 * shortest; at least five crossings of hers with hers.
 */
export const lagoonReads = (tracks: LagoonTrack[], cs: LagoonCross[]): boolean => {
  const hers = tracks.filter((t) => t.who === 0);
  if (hers.length !== 6 || hers.some((t) => t.pts.length < 2)) return false;
  const lens = hers.map((t) => trackLen(t.pts));
  for (let d = 1; d < 6; d++) if ((lens[d] as number) >= (lens[d - 1] as number)) return false;
  const ends = hers.map((t) => t.pts[t.pts.length - 1] as Pt);
  for (let i = 0; i < 6; i++) for (let j = i + 1; j < 6; j++) if (len((ends[i] as Pt).x - (ends[j] as Pt).x, (ends[i] as Pt).y - (ends[j] as Pt).y) < 2600) return false;
  // Each end clear of the other tracks (it's read as an end, not a crossing).
  for (let i = 0; i < 6; i++) {
    const e = ends[i] as Pt;
    for (let k = 0; k < tracks.length; k++) {
      if (k === i) continue;
      const t = tracks[k] as LagoonTrack;
      const near = t.who === 0 ? 650 : 500;
      for (let p = 1; p < t.pts.length; p++) if (segDist2(e.x, e.y, t.pts[p - 1] as Pt, t.pts[p] as Pt) < near * near) return false;
    }
  }
  for (let i = 0; i < cs.length; i++) {
    const c = cs[i] as LagoonCross;
    if (len(c.at.x, c.at.y) > LAGOON_ICE - 500) return false;
    const ch = (tracks[c.a] as LagoonTrack).who === 0 && (tracks[c.b] as LagoonTrack).who === 0;
    for (let j = i + 1; j < cs.length; j++) {
      const o = cs[j] as LagoonCross;
      const apart = ch && (tracks[o.a] as LagoonTrack).who === 0 && (tracks[o.b] as LagoonTrack).who === 0 ? 650 : 400;
      if (len(c.at.x - o.at.x, c.at.y - o.at.y) < apart) return false;
    }
    for (const e of ends) if (len(c.at.x - e.x, c.at.y - e.y) < 900) return false;
  }
  const herCs = cs.filter((c) => (tracks[c.a] as LagoonTrack).who === 0 && (tracks[c.b] as LagoonTrack).who === 0);
  if (herCs.length < 5 || herCs.length > 11) return false;
  for (let d = 0; d < 5; d++) if (!herCs.some((c) => (c.a === d || c.b === d) && c.top !== d)) return false;
  // The Wolf walks over most of hers (not a help to order them), but leaves the lagoon readable.
  return cs.length <= 20;
};

export const lagoonLayout = (seed: number, depth = 0): LagoonLayout => {
  const rng = seedRng(deriveSeed(seed, 0x1a60 + depth));
  let tracks = drawTracks(rng);
  let cs = crossingsOf(tracks);
  // (About 450 draws on the median, 3000 at the 99th; never more than 6000 over a thousand seeds: a few milliseconds.)
  for (let k = 0; k < 8000 && !lagoonReads(tracks, cs); k++) {
    tracks = drawTracks(rng);
    cs = crossingsOf(tracks);
  }
  if (!lagoonReads(tracks, cs) && depth < 4) return lagoonLayout(seed, depth + 1);
  // The letters at her ends: shuffled, so A is never the first day.
  const letters = [0, 1, 2, 3, 4, 5];
  for (let i = 5; i > 0; i--) {
    const j = rngInt(rng, i + 1);
    const t = letters[i] as number;
    letters[i] = letters[j] as number;
    letters[j] = t;
  }
  tracks.forEach((t, i) => {
    if (t.who === 0) t.letter = letters[i] as number;
  });
  const ends: Pt[] = [];
  for (const t of tracks) if (t.who === 0) ends[t.letter] = { ...(t.pts[t.pts.length - 1] as Pt) };
  const last = (tracks[5] as LagoonTrack).letter;
  const R = LAGOON_RADIUS;
  const ice: Hex[] = [];
  const floor = new Set<string>();
  for (const h of allCells(R - 1)) {
    const p = hexToWorld(h.q, h.r);
    floor.add(key(h));
    if (len(p.x, p.y) <= LAGOON_ICE + 300 && p.y < SHORE_LAGOON) ice.push(h);
  }
  const shore: Pt[] = [
    { x: -6500, y: 8600 },
    { x: 0, y: 10200 },
    { x: 6500, y: 8600 },
    { x: -9800, y: 2000 },
    { x: 9800, y: 2000 },
  ];
  return { rocks: rocksBut(R, floor), ice, tracks, crossings: cs, ends, last, shore, start: { x: 0, y: 8800 } };
};

/** The tracks a crossing joins, as letters (−1 not hers: the Wolf's 6, the pack's 7). */
export const crossLetter = (tracks: LagoonTrack[], i: number): number => {
  const t = tracks[i];
  return !t ? -1 : t.who === 0 ? t.letter : t.who === 1 ? 6 : 7;
};

/* ---------- act 3: Sigrun's last night ---------- */

export interface SealLayout {
  rocks: Hex[];
  fire: Pt;
  holes: Pt[];
  rim: Pt[];
  start: Pt;
  sealAt: Pt;
  howlers: Pt[];
}

const HOLES: Pt[] = [
  { x: -6500, y: -2600 },
  { x: 6000, y: -3500 },
  { x: 500, y: -8000 },
];

export const sealLayout = (seed: number): SealLayout => {
  const rng = seedRng(deriveSeed(seed, 0x5ea1));
  const R = SEAL_RADIUS;
  // The open cracks (the sea under the ice) are the board's own water: a cell each, and a neighbour by the seed.
  const water = new Set<string>();
  const holes = HOLES.map((h) => ({ x: h.x + rngRange(rng, -400, 400), y: h.y + rngRange(rng, -300, 300) }));
  const floor = new Set<string>();
  for (const h of allCells(R)) {
    const p = hexToWorld(h.q, h.r);
    if (holes.some((o) => len(p.x - o.x, p.y - o.y) <= 700)) {
      water.add(key(h));
      continue;
    }
    floor.add(key(h));
  }
  const rim: Pt[] = [];
  for (let k = 0; k < 8; k++) {
    // (A ring of eight points round the island's edge, integer: the hexes' corners scaled.)
    const c = hexToWorld([10, 0, -10, -10, 0, 10, 7, -7][k] as number, [0, 10, 10, 0, -10, -10, 3, -3][k] as number);
    rim.push({ x: imin(10000, imax(-10000, c.x)), y: imin(9500, imax(-9500, c.y)) });
  }
  return {
    rocks: rocksBut(R, floor),
    fire: { x: 0, y: 2598 },
    holes,
    rim,
    start: { x: 0, y: 4300 },
    sealAt: { x: 0, y: -5200 },
    howlers: [
      { x: -7500, y: 3500 },
      { x: 7500, y: 2600 },
    ],
  };
};
